import { Injectable } from '@angular/core';
import { HubConnection } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import * as signalR from '@microsoft/signalr';
import { SecurityService } from './security.service';

import { ApplicationModel } from '@nx-ahs/shared/models';
import { NotificationService } from '@nx-ahs/shared/services';
import { ConfirmationModalService } from '@nx-ahs/shared/components';
import moment from 'moment';
import { EncounterService } from './encounter.service';
import { Encounter } from '../../shared/models/enctr/encounter';
import { BroadcastNotificationService } from './broadcast-notification.service';
import { BrowserNotificationService } from './browser-notification.service';
import { AuthenticationResult } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { MailService } from '@nx-ahs/mail';
import { map } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class SignalrService {
	private gameHubConnection: HubConnection;
	private encounterHubConnection: HubConnection;
	private notificationHubConnection: HubConnection;
	private mailConsoleHubConnection: HubConnection;
	private applicationModel: ApplicationModel;

	constructor(
		private securityService: SecurityService,
		private notificationService: NotificationService,
		private encounterService: EncounterService,
		private browserNotificationService: BrowserNotificationService,
		private broadcastNotificationService: BroadcastNotificationService,
		private mailService: MailService,
		private confirmationModalService: ConfirmationModalService,
		private router: Router
	) {}

	public initialize(): void {
		this.applicationModel = this.securityService.getCurrentApp();
		//this.initializeGameHub();
		this.initializeEncounterHub();
		this.initializeTaskHub();
		this.initializeMailConsoleHub();
	}

	//#region Encounter
	private initializeEncounterHub(): void {
		if (!this.encounterHubConnection && this.securityService.msalHasAccount) {
			// Create hub
			const url = environment.apiUrl + 'EncounterHub';
			this.encounterHubConnection = new signalR.HubConnectionBuilder()
				.withUrl(url, {
					accessTokenFactory: () =>
						this.securityService
							.acquireToken()
							?.pipe(
								map((x: AuthenticationResult) => {
									return x.accessToken;
								})
							)
							.toPromise()
				})
				.build();

			// Start hub
			this.startHub(this.encounterHubConnection, 'Encounter', true);

			// Setup Game Hub Event Handlers
			this.encounterHubConnection.on('LaunchCallEncounter', (data) => {
				this.launchEncounter(data);
			});
		}
	}

	private launchEncounter(data: any): void {
		const encounter = Encounter.fromData(data);
		this.encounterService.cti_initialize(encounter);
	}
	//#endregion Encounter

	//#region Game
	private initializeGameHub(): void {
		if (!this.gameHubConnection && this.securityService.msalHasAccount) {
			// Create hub
			const url = environment.apiUrl + 'GameHub';
			this.gameHubConnection = new signalR.HubConnectionBuilder()
				.withUrl(url, {
					accessTokenFactory: () =>
						this.securityService
							.acquireToken()
							?.pipe(
								map((x: AuthenticationResult) => {
									return x.accessToken;
								})
							)
							.toPromise()
				})
				.build();

			// Start hub
			this.startHub(this.gameHubConnection, 'Game', true);

			// Setup Game Hub Event Handlers
			this.gameHubConnection.on('KudosReceived', (data) => {
				this.kudoReceived(data);
			});
		}
	}

	private kudoReceived(data: any): void {
		this.notificationService.addNotification(
			`Kudos Received: ${data.kudo.name}`,
			`${data.createdBy.fullName} sent you kudos!`,
			moment(),
			false
		);
	}

	//#endregion

	//#region Notification

	private initializeTaskHub(): void {
		if (!this.notificationHubConnection && this.securityService.msalHasAccount) {
			// Create hub
			const url = environment.apiUrl + 'NotificationHub';
			this.notificationHubConnection = new signalR.HubConnectionBuilder()
				.withUrl(url, {
					accessTokenFactory: () =>
						this.securityService
							.acquireToken()
							?.pipe(
								map((x: AuthenticationResult) => {
									return x.accessToken;
								})
							)
							.toPromise()
				})
				.build();

			// Start hub
			this.startHub(this.notificationHubConnection, 'Notification', true);

			// Setup Hub Event Handlers
			this.notificationHubConnection.on('SendNotification', (data) => {
				if (data.appId == this.applicationModel.application.applicationId) {
					this.addNotification(data.notification);
				}
			});

			this.notificationHubConnection.on('AlertUserOfOpenTask', (data) => {
				const { taskId, otherUsers } = data;

				let message = ``;
				if (otherUsers?.length == 1) {
					message = `This task (#${taskId}) is currently open and in use by ${otherUsers[0]}.`;
				} else if (otherUsers?.length > 1) {
					message = `This task (#${taskId}) is currently open and in use by multiple users.<br/><br/>It is currently open by:`;

					otherUsers.forEach((user) => {
						message += `<br/>- ${user}`;
					});
				}

				message += '<br/><br/>Any changes made could be overridden.';

				this.confirmationModalService.showConfirmationDialog('Task Already Open', message, true, false);
			});
		}
	}

	private browserNotificationPromise: Promise<Notification>;

	private addNotification(notification: any): void {
		if (notification.isBell) {
			this.notificationService.addNotification(
				`${notification.subject}`,
				`${notification.message}`,
				moment(),
				false,
				notification.url,
				notification.sidebar,
				notification.sidebarParam
			);
		}

		if (notification.isBrowser) {
			if (notification.isBroadcastNotification) {
				this.broadcastNotificationService.createBroadcastNotification(notification);
			} else {
				this.browserNotificationPromise = this.browserNotificationService.createBrowserNotification(
					`${notification.subject}`,
					`${notification.message}`,
					false
				);

				this.browserNotificationPromise.then((result) => {
					if (result) {
						result.onclick = (event) => {
							if (notification.sidebar) {
								this.router.navigate([
									{ outlets: { sidebar: [notification.sidebar, notification.sidebarParam] } }
								]);
							} else if (notification.url) {
								this.router.navigate([notification.url]);
							} else if (notification.click) {
								notification.click(null);
							}
							result.close();
						};
					}
				});
			}
		}
	}

	//#endregion

	//#region Mail Console

	private initializeMailConsoleHub(): void {
		if (!this.mailConsoleHubConnection && this.securityService.msalHasAccount) {
			// Create hub
			const url = environment.apiUrl + 'MailConsoleHub';
			this.mailConsoleHubConnection = new signalR.HubConnectionBuilder()
				.withUrl(url, {
					accessTokenFactory: () =>
						this.securityService
							.acquireToken()
							?.pipe(
								map((x: AuthenticationResult) => {
									return x.accessToken;
								})
							)
							.toPromise()
				})
				.build();

			// Start hub
			this.startHub(this.mailConsoleHubConnection, 'MailConsole', true);

			// Setup Game Hub Event Handlers
			this.mailConsoleHubConnection.on('NotifyUsersOfNewBatches', (data) => {
				this.mailService.newMailBatchesNotificationEvent.emit(data);
			});
		}
	}

	//#endregion

	private startHub(hub: HubConnection, name: string, autoRestart: boolean): void {
		// Start the hub
		hub.start()
			.then(() => {
				console.log(`${name} Hub Connection started`);
			})
			.catch((err) => {
				console.log(`${name} Hub Connection error - ${err}`);
			});

		// If hub closes then attempt a restart after 5 seconds if autorestart is true
		if (autoRestart) {
			hub.onclose(() => {
				setTimeout(() => {
					this.startHub(hub, name, false);
				}, 5000);
			});
		}
	}
}
